import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, formatDistanceToNow } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import AndroidIcon from "@material-ui/icons/Android";
import VisibilityIcon from "@material-ui/icons/Visibility";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import UndoRoundedIcon from "@material-ui/icons/UndoRounded";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import TicketMessagesDialog from "../TicketMessagesDialog";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ScheduleModal from "../ScheduleModal";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    paddingTop: 6,
    paddingBottom: 6,
    paddingRight: 4,
    "&.Mui-selected": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.07)"
        : "rgba(37,118,210,0.08)",
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      "&:hover": {
        backgroundColor: theme.palette.type === "dark"
          ? "rgba(255,255,255,0.1)"
          : "rgba(37,118,210,0.12)",
      },
    },
  },

  pendingTicket: {
    cursor: "unset",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  lastMessageTime: {
    flexShrink: 0,
    fontSize: "0.72rem",
    marginLeft: 6,
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },

  contactLastMessage: {
    flex: 1,
    minWidth: 0,
  },

  badgeStyle: {
    color: "white",
    backgroundColor: theme.palette.primary.main,
  },

  unreadBadge: {
    flexShrink: 0,
    marginLeft: 6,
    alignSelf: "center",
  },

  ticketQueueColor: {
    flex: "none",
    width: "4px",
    height: "100%",
    position: "absolute",
    top: "0%",
    left: "0%",
    borderRadius: "0 2px 2px 0",
  },

  acceptButton: {
    fontSize: "0.65rem",
    padding: "2px 8px",
    marginLeft: 8,
    flexShrink: 0,
    alignSelf: "center",
  },

  optionsButton: {
    padding: 4,
    marginLeft: 2,
    flexShrink: 0,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
    },
  },

  secondaryRow: {
    display: "flex",
    alignItems: "center",
  },
}));

const TicketListItemCustom = ({ ticket, onTicketClick }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [ticketQueueColor, setTicketQueueColor] = useState(null);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const { profile } = user;

  useEffect(() => {
    setTicketQueueColor(ticket.queue?.color || "#7C7C7C");
    return () => {
      isMounted.current = false;
    };
  }, [ticket]);

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, { status: "open", userId: user?.id });

      let settingIndex;
      try {
        const { data } = await api.get("/settings/");
        settingIndex = data.filter((s) => s.key === "sendGreetingAccepted");
      } catch (err) {
        toastError(err);
      }

      if (settingIndex && settingIndex.length > 0 && settingIndex[0].value === "enabled" && !ticket.isGroup) {
        await handleSendMessage(ticket.id);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
    if (onTicketClick) onTicketClick();
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleSendMessage = async (id) => {
    const msg = `{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com seu atendimento!`;
    try {
      await api.post(`/messages/${id}`, {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: `*Mensagem Automática:*\n${msg.trim()}`,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const handleResolveTicket = async () => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "closed",
        userId: user?.id,
        useIntegration: false,
        promptId: null,
        integrationId: null,
      });
      history.push("/tickets");
    } catch (err) {
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
  };

  const handleReturnTicket = async () => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "pending",
        userId: null,
        useIntegration: ticket.useIntegration,
        promptId: ticket.promptId,
        integrationId: ticket.integrationId,
      });
      history.push("/tickets");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTicket = async () => {
    try {
      await api.delete(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSelectTicket = (ticket) => {
    if (onTicketClick) onTicketClick();
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleOpenMenu = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const lastMessagePreview = ticket.lastMessage?.includes("data:image/png;base64")
    ? "Localização"
    : ticket.lastMessage;

  return (
    <React.Fragment key={ticket.id}>
      <TicketMessagesDialog
        open={openTicketMessageDialog}
        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      />
      <ConfirmationModal
        title={`Deletar ticket #${ticket.id} de ${ticket.contact.name}?`}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteTicket}
      >
        {i18n.t("ticketOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>
      <TransferTicketModalCustom
        modalOpen={transferModalOpen}
        onClose={() => { if (isMounted.current) setTransferModalOpen(false); }}
        ticketid={ticket.id}
      />
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={() => { setScheduleModalOpen(false); }}
        aria-labelledby="form-dialog-title"
        contactId={ticket.contact?.id}
      />

      <ListItem
        dense
        button
        onClick={() => {
          if (ticket.status === "pending") return;
          handleSelectTicket(ticket);
        }}
        selected={Boolean(ticketId && (ticketId === ticket.uuid || +ticketId === ticket.id))}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        <Tooltip arrow placement="right" title={ticket.queue?.name || "Sem Fila"}>
          <span
            style={{ backgroundColor: ticketQueueColor || "#7C7C7C" }}
            className={classes.ticketQueueColor}
          />
        </Tooltip>

        <ListItemAvatar>
          <Avatar
            style={{ width: 42, height: 42 }}
            src={ticket?.contact?.profilePicUrl}
            alt={ticket?.contact?.name}
          >
            {ticket?.contact?.name?.charAt(0).toUpperCase()}
          </Avatar>
        </ListItemAvatar>

        <ListItemText
          disableTypography
          primary={
            <span className={classes.contactNameWrapper}>
              <Typography
                noWrap
                component="span"
                variant="body2"
                color="textPrimary"
                style={{ fontWeight: 500, flex: 1, minWidth: 0 }}
              >
                {ticket.contact.name}
                {ticket.chatbot && (
                  <Tooltip title="Chatbot">
                    <AndroidIcon
                      fontSize="small"
                      style={{ color: grey[500], marginLeft: 4, verticalAlign: "middle", fontSize: 14 }}
                    />
                  </Tooltip>
                )}
                {profile === "admin" && (
                  <Tooltip title="Espiar Conversa">
                    <VisibilityIcon
                      onClick={(e) => { e.stopPropagation(); setOpenTicketMessageDialog(true); }}
                      fontSize="small"
                      style={{ color: blue[400], cursor: "pointer", marginLeft: 6, verticalAlign: "middle", fontSize: 14 }}
                    />
                  </Tooltip>
                )}
              </Typography>
              {ticket.lastMessage && (
                <Tooltip
                  title={format(parseISO(ticket.updatedAt), "dd/MM/yyyy HH:mm")}
                  placement="left"
                >
                  <span className={classes.lastMessageTime}>
                    {formatDistanceToNow(parseISO(ticket.updatedAt), {
                      addSuffix: false,
                      locale: ptBR,
                    })}
                  </span>
                </Tooltip>
              )}
            </span>
          }
          secondary={
            <span className={classes.secondaryRow}>
              <Typography
                className={classes.contactLastMessage}
                noWrap
                component="span"
                variant="body2"
                color="textSecondary"
                style={{ fontSize: "0.8rem" }}
              >
                <MarkdownWrapper>{lastMessagePreview}</MarkdownWrapper>
              </Typography>

              {ticket.unreadMessages > 0 && (
                <Badge
                  className={classes.unreadBadge}
                  badgeContent={ticket.unreadMessages}
                  classes={{ badge: classes.badgeStyle }}
                />
              )}

              {ticket.status === "pending" && (
                <ButtonWithSpinner
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.acceptButton}
                  loading={loading}
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleAcepptTicket(ticket.id);
                  }}
                >
                  {i18n.t("ticketsList.buttons.accept")}
                </ButtonWithSpinner>
              )}

              <IconButton
                className={classes.optionsButton}
                size="small"
                onClick={handleOpenMenu}
              >
                <KeyboardArrowDownIcon style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          }
        />
      </ListItem>

      <Menu
        anchorEl={menuAnchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        {ticket.status === "open" && (
          <Tooltip title="Marcar atendimento como resolvido" placement="left">
            <MenuItem onClick={() => { handleResolveTicket(); handleCloseMenu(); }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="primary" />
              </ListItemIcon>
              Resolver
            </MenuItem>
          </Tooltip>
        )}
        {ticket.status === "open" && (
          <Tooltip title="Devolver ticket para a fila de pendentes" placement="left">
            <MenuItem onClick={() => { handleReturnTicket(); handleCloseMenu(); }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <UndoRoundedIcon fontSize="small" />
              </ListItemIcon>
              {i18n.t("messagesList.header.buttons.return")}
            </MenuItem>
          </Tooltip>
        )}
        <Tooltip title="Agendar mensagem ou lembrete para este contato" placement="left">
          <MenuItem onClick={() => { handleCloseMenu(); setScheduleModalOpen(true); }}>
            <ListItemIcon style={{ minWidth: 32 }}>
              <EventAvailableIcon fontSize="small" />
            </ListItemIcon>
            {i18n.t("ticketOptionsMenu.schedule")}
          </MenuItem>
        </Tooltip>
        <Tooltip title="Transferir atendimento para outro agente ou fila" placement="left">
          <MenuItem onClick={() => { handleCloseMenu(); setTransferModalOpen(true); }}>
            <ListItemIcon style={{ minWidth: 32 }}>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            {i18n.t("ticketOptionsMenu.transfer")}
          </MenuItem>
        </Tooltip>
        {profile === "admin" && (
          <Tooltip title="Excluir permanentemente este ticket" placement="left">
            <MenuItem onClick={() => { handleCloseMenu(); setConfirmationOpen(true); }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </ListItemIcon>
              <span style={{ color: "red" }}>{i18n.t("ticketOptionsMenu.delete")}</span>
            </MenuItem>
          </Tooltip>
        )}
      </Menu>

      <Divider variant="inset" component="li" />
    </React.Fragment>
  );
};

export default TicketListItemCustom;
